// AI-CONTEXT: See docs/features/participation-value-dashboard-card.md
// AI-CONTEXT: See docs/features/msi-effect-monetisation.md
// AI-CONTEXT: See docs/patterns.md#dashboard-static-card
import { useMemo } from 'react'
import { useHistory } from 'react-router-dom'
import { client } from '../../hooks/Client'
import { useFirestoreGeneral } from '../../firebase/useFirestore'
import formatEuro from '../../helpers/formatEuro'
import useParticipationParameters from '../../hooks/useParticipationParameters'
import useMsiMonetaryTotal from '../../hooks/useMsiMonetaryTotal'
import useMaterialValue from '../../hooks/useMaterialValue'
import sumCurrentSocietalValue from '../../helpers/participationHelpers/sumCurrentSocietalValue'
import useParticipationBirthDates from '../../hooks/useParticipationBirthDates'
import { isPostsModel } from '../../helpers/participationHelpers/calculateParticipationImpact'
import dataSourceErrorMessage from '../../helpers/dataSourceErrorMessage'
import notAttributedText from '../../helpers/msiMonetisationHelpers/notAttributedText'
import StaticCardLabel from './StaticCardLabel'
import KpiValue from './KpiValue'

/**
 * Compact KPI tile: the live total Maatschappelijke waarde — three streams,
 * computed separately (double-counting guard) and only added here; the detail
 * page shows them as distinct sections. The trend sparkline was deprecated and
 * removed.
 *
 *   1. participatieladder over actieve deelnemers
 *   2. MSI-effect-monetisatie
 *   3. maatschappelijke besparing van materialen en coachadviezen
 *
 * Alle drie zonder periodegrenzen: deze kaart toont de opgebouwde waarde sinds
 * de start, niet één boekjaar. Het kengetal Impact per euro gebruikt dezelfde
 * stromen wél boekjaar-gescopet.
 *
 * Van de materialen telt alleen de maatschappelijke besparing mee — de
 * besparing op de energierekening is geld van de bewoner en de bespaarde CO2
 * heeft een eigen eenheid.
 *
 * Ladder en MSI tellen toegerekend (impactclaim, looptijd, drop-off,
 * disconto — #343). Wie zonder duur of uren niet doorgerekend kon worden telt
 * niet als 0 mee maar wordt hier geteld; de detailpagina noemt ze bij naam.
 * AI-CONTEXT: See docs/methodiek-impact-per-euro.md § 1.3
 * AI-CONTEXT: See docs/features/msi-rekenregels-monetarisering.md (R2)
 */
const DashboardParticipationValueCard = () => {
    const history = useHistory()
    const { parameters, loading: parametersLoading, error: parametersError } = useParticipationParameters()
    const personas = useFirestoreGeneral('Personas', 'Status', 'active')
    const {
        attributedTotal: msiTotal,
        notAttributedCount: msiNotAttributedCount,
        notAttributedReasons: msiNotAttributedReasons,
        loading: msiLoading,
        error: msiError,
    } = useMsiMonetaryTotal()
    // De standaard duur en uren van de organisatie; '' zolang hij laadt.
    const compagnyMeta = useFirestoreGeneral('CompagnyMeta', 'CompagnyID', client)
    const doseLoaded = Array.isArray(compagnyMeta)
    const orgDefault = doseLoaded ? (compagnyMeta[0]?.DefaultInterventionDose || null) : null
    const { value: materialValue, loading: materialLoading, error: materialError } = useMaterialValue()
    // Geboortedatums voor de leeftijdsovergang (alleen onder 2026.3).
    const { birthDates, error: birthDatesError } = useParticipationBirthDates(isPostsModel(parameters))

    // Laden is een eigen toestand, geen ontbrekende waarde. Alle drie de
    // stromen (plus de deelnemers) tellen mee: zolang er één onderweg is, is
    // het totaal 'nog niet bekend'. De hook geeft '' zolang hij laadt — een
    // lege array is een geladen leeg resultaat en betekent iets anders.
    // AI-CONTEXT: See docs/pitfalls.md#ui-empty-state-claims-absence-while-loading
    const loading = parametersLoading === true
        || personas === ''
        || msiLoading === true
        || materialLoading === true
        || birthDates === null
        || !doseLoaded

    // null until the model parameters have loaded — fail visibly rather than
    // recompute the total against bundled defaults.
    const ladder = useMemo(
        () => (parameters && birthDates && doseLoaded
            ? sumCurrentSocietalValue(Array.isArray(personas) ? personas : [], parameters, birthDates, orgDefault)
            : null),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [personas, parameters, birthDates, doseLoaded, JSON.stringify(orgDefault)],
    )

    // Alle drie de stromen moeten er zijn voordat er iets wordt getoond — een
    // deeltotaal doet zich stilzwijgend als het geheel voor.
    const total = ladder === null || msiTotal === null || materialValue === null
        ? null
        : ladder.attributedValue + msiTotal + materialValue

    const notAttributedCount = (ladder ? ladder.notAttributedCount : 0) + (msiNotAttributedCount || 0)
    const notAttributedReasons = { ...(ladder ? ladder.notAttributedReasons : {}) }
    Object.entries(msiNotAttributedReasons || {}).forEach(([reason, count]) => {
        notAttributedReasons[reason] = (notAttributedReasons[reason] || 0) + count
    })

    /** @param {React.SyntheticEvent} e */
    const naarRekenwijze = (e) => {
        // Niet ook de kaart zelf laten klikken.
        e.stopPropagation()
        history.push(`/${client}/maatschappelijke-waarde#rekenwijze`)
    }

    // De melding noemt de stroom die wegviel. Eén tekst voor alle drie wijst
    // bij twee ervan de verkeerde kant op — zie de helper.
    const errorMessage = dataSourceErrorMessage([
        ['rekenparameters', parametersError],
        ['effectwaarden', msiError],
        ['materiaalwaarden', materialError],
        ['geboortedatums', birthDatesError],
    ])

    return (
        <div
            className='participation-value-card participation-value-card--clickable participation-value-card--dense'
            role='button'
            tabIndex={0}
            onClick={() => history.push(`/${client}/maatschappelijke-waarde`)}
            onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault()
                    history.push(`/${client}/maatschappelijke-waarde`)
                }
            }}
        >
            <StaticCardLabel title='Maatschappelijke waarde' />
            {errorMessage ? (
                <p className='participation-value-error'>{errorMessage}</p>
            ) : (
                <KpiValue loading={loading} value={total === null ? null : formatEuro(total)} />
            )}
            {!errorMessage && total !== null && notAttributedCount > 0 && (
                <p className='participation-value-note'>{notAttributedText(notAttributedCount, notAttributedReasons)}</p>
            )}
            {!errorMessage && total !== null && (
                <p className='participation-value-note'>
                    Na impactclaim, looptijd en disconto.
                    <button
                        type='button'
                        className='participation-value-method-link'
                        onClick={naarRekenwijze}
                        onKeyDown={(e) => e.stopPropagation()}
                    >
                        Rekenwijze →
                    </button>
                </p>
            )}
            <span className='participation-value-more'>Meer info →</span>
        </div>
    )
}

export default DashboardParticipationValueCard
